import type { AnyAnswer } from '../../api/types.ts'
import { type Refusal, useAttemptStore } from './answerStore.ts'

const DEBOUNCE_MS = 600
/**
 * A save that has not answered by now is dead: on a phone that flipped between Wi-Fi and mobile data
 * the request hangs until the edge gives up at 60 s, and she sees "Saving" the whole time.
 */
export const SAVE_TIMEOUT_MS = 15_000
/** Waits before the automatic retries of failed answers, in turn; the last one repeats. */
export const RETRY_BACKOFF_MS = [2_000, 5_000, 10_000, 30_000] as const

/** `signal` is aborted when the save times out, so the request is dropped instead of left hanging. */
type Sender = (exerciseId: string, answer: AnyAnswer, signal: AbortSignal) => Promise<unknown>

/**
 * The owner of every outstanding learner response.
 *
 * The debounce used to live in the question card, and "Hand it in" knew nothing about it: the
 * last answer typed before tapping submit was still sitting in a 600 ms timer and got scored
 * blank. Timers live here so the page can await them before submitting, and so a failed PUT
 * can be retried rather than reported once and forgotten.
 */

let sender: Sender | null = null

const timers = new Map<string, ReturnType<typeof setTimeout>>()
const inFlight = new Set<string>()
const again = new Set<string>()
const running = new Map<string, Promise<void>>()
/**
 * Questions whose last send ended without a clear answer from the server (timed out, connection
 * lost, a gateway error): that request may still be travelling and commit after a newer one. The
 * api keeps whichever write lands last, so before the hand-in each of these is sent once more.
 */
const maybeLate = new Set<string>()
let retryTimer: ReturnType<typeof setTimeout> | undefined
/** Automatic retry rounds since every answer last reached the server: picks the next wait. */
let rounds = 0

/** The page's way to the server while an attempt is open; none, and nothing is sent or retried. */
export const setSender = (fn: Sender | null) => {
	sender = fn
	if (!fn) {
		clearTimeout(retryTimer)
		retryTimer = undefined
		rounds = 0
		maybeLate.clear()
	}
}

/** Statuses that say "try later", not "no": everything else in the 4xx range is a final answer. */
const TRANSIENT = new Set([408, 425, 429, 499])

/**
 * Whether the server refused this answer for good. A connection that failed or timed out, a 5xx and
 * the "slow down" statuses are not a refusal: those are retried. A refusal is never retried: sending
 * the same thing again gets the same answer, forever, and would hold the hand-in back for good.
 * Read off `status` so the queue needs nothing from the api layer.
 */
function refusal(err: unknown): Refusal | null {
	const status = (err as { status?: unknown } | null)?.status
	if (typeof status !== 'number' || status < 400 || status >= 500 || TRANSIENT.has(status))
		return null
	return status === 409 ? 'closed' : 'refused'
}

function send(exerciseId: string, answer: AnyAnswer): Promise<unknown> {
	// The page left between the check in run() and here: nothing was sent, so it must not count as sent.
	if (!sender) return Promise.reject(new Error('No sender'))
	const call = sender
	return new Promise((resolve, reject) => {
		const ctl = new AbortController()
		const timer = setTimeout(() => {
			ctl.abort()
			reject(new Error('The save timed out'))
		}, SAVE_TIMEOUT_MS)
		call(exerciseId, answer, ctl.signal).then(
			(v) => {
				clearTimeout(timer)
				resolve(v)
			},
			(e) => {
				clearTimeout(timer)
				reject(e)
			},
		)
	})
}

/** Failed answers go again on their own, a little later each round, for as long as the page is open. */
function scheduleRetry() {
	if (retryTimer || !sender) return
	const wait = RETRY_BACKOFF_MS[Math.min(rounds, RETRY_BACKOFF_MS.length - 1)]
	rounds++
	retryTimer = setTimeout(() => {
		retryTimer = undefined
		void sendOutstanding()
	}, wait)
}

async function run(exerciseId: string): Promise<void> {
	const answer = useAttemptStore.getState().answers[exerciseId]
	if (answer === undefined || !sender) return

	if (inFlight.has(exerciseId)) {
		again.add(exerciseId)
		return running.get(exerciseId)
	}

	inFlight.add(exerciseId)
	const promise = (async () => {
		try {
			await send(exerciseId, answer)
			// Only clear if she has not typed again since; the newer text is still unsent.
			if (!again.has(exerciseId)) useAttemptStore.getState().markSent(exerciseId)
			if (Object.keys(useAttemptStore.getState().failed).length === 0) rounds = 0
		} catch (err) {
			// `again` stays: whoever asked for a newer send (her typing, the hand-in) gets one more try.
			const why = refusal(err)
			if (why) {
				maybeLate.delete(exerciseId)
				// Newer text on its way (typed, or asked for again) is judged on its own.
				if (!again.has(exerciseId) && !timers.has(exerciseId)) {
					useAttemptStore.getState().markRefused(exerciseId, why)
				}
			} else {
				maybeLate.add(exerciseId)
				useAttemptStore.getState().markFailed(exerciseId)
				scheduleRetry()
			}
		} finally {
			inFlight.delete(exerciseId)
			running.delete(exerciseId)
		}
		if (again.delete(exerciseId)) await run(exerciseId)
	})()

	running.set(exerciseId, promise)
	return promise
}

export function scheduleResponse(exerciseId: string, answer: AnyAnswer) {
	useAttemptStore.getState().answer(exerciseId, answer)
	clearTimeout(timers.get(exerciseId))
	timers.set(
		exerciseId,
		setTimeout(() => {
			timers.delete(exerciseId)
			void run(exerciseId)
		}, DEBOUNCE_MS),
	)
}

/** Nothing owed to the server: no answer waiting or failing. Refused answers are not owed. */
const allSent = () => {
	const s = useAttemptStore.getState()
	return Object.keys(s.unsent).length + Object.keys(s.failed).length === 0
}

/**
 * Everything outstanding, awaited, failed answers included: submit cannot run before this resolves.
 * True when every answer is now on the server (or refused for good); false when some could not be
 * sent, and then all of them are still kept on the device.
 */
export async function flushResponses(): Promise<boolean> {
	for (const t of timers.values()) clearTimeout(t)
	timers.clear()
	const s = useAttemptStore.getState()
	const ids = new Set([...Object.keys(s.unsent), ...Object.keys(s.failed), ...inFlight])
	await Promise.all([...ids].map((id) => run(id)))
	return allSent()
}

/**
 * What the hand-in waits for: everything outstanding sent (`flushResponses`), then each question
 * whose earlier send ended unclear sent once more, one at a time, now that nothing else is in
 * flight, so a stalled older request that commits late cannot be the last word. False when
 * something could not be sent.
 * ponytail: a request delayed in the network past this last resend can still land after it. The real
 * fix is a per-question sequence number, compared in the api's upsert (routes.ts, PUT responses).
 */
export async function sendBeforeSubmit(): Promise<boolean> {
	if (!(await flushResponses())) return false
	for (const id of [...maybeLate]) {
		const s = useAttemptStore.getState()
		if (s.answers[id] !== undefined && !s.refused[id]) {
			await run(id)
			if (!allSent()) return false
		}
		maybeLate.delete(id)
	}
	return allSent()
}

/**
 * Every answer that is not on the server and not on its way: failed ones, and ones kept on the device
 * from before a reload. Not the ones still in the typing window or already in flight.
 */
export function sendOutstanding(): Promise<void> {
	clearTimeout(retryTimer)
	retryTimer = undefined
	const s = useAttemptStore.getState()
	const ids = new Set([...Object.keys(s.unsent), ...Object.keys(s.failed)])
	const due = [...ids].filter((id) => !inFlight.has(id) && !timers.has(id))
	// Resolves when everything that was on its way, or is now, has settled.
	return Promise.all([
		...due.map((id) => run(id)),
		...[...inFlight].map((id) => running.get(id)),
	]).then(() => undefined)
}

export function retryResponse(exerciseId: string): Promise<void> {
	clearTimeout(timers.get(exerciseId))
	timers.delete(exerciseId)
	return run(exerciseId) ?? Promise.resolve()
}
